import { useState, useEffect } from 'react';
import { api } from '../../../lib/api';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

interface WidgetConfig {
  primary_color: string;
  welcome_message: string;
  position: string;
}

interface WidgetStatus {
  enabled: boolean;
  widget_key: string | null;
  embed_code: string | null;
  config: WidgetConfig;
}

export default function WidgetSettings() {
  const [status, setStatus] = useState<WidgetStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [color, setColor] = useState('#2563eb');
  const [welcomeMessage, setWelcomeMessage] = useState('Hi! How can we help you today?');
  const [position, setPosition] = useState('bottom-right');

  const fetchStatus = async () => {
    try {
      const data = await api.get<WidgetStatus>('/stores/widget/status');
      setStatus(data);
      setColor(data.config.primary_color);
      setWelcomeMessage(data.config.welcome_message);
      setPosition(data.config.position);
    } catch {
      // Widget endpoints not available yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleEnable = async () => {
    setSaving(true);
    try {
      await api.post('/stores/widget/enable');
      await fetchStatus();
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setSaving(true);
    try {
      await api.post('/stores/widget/disable');
      await fetchStatus();
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/stores/widget/settings', {
        primary_color: color,
        welcome_message: welcomeMessage,
        position,
      });
      await fetchStatus();
    } finally {
      setSaving(false);
    }
  };

  const copyEmbed = () => {
    if (status?.embed_code) {
      navigator.clipboard.writeText(status.embed_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-ink-3">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable */}
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold text-ink">Web Chat Widget</h3>
            <p className="mt-1 text-sm text-ink-2">
              Add a live chat widget to your website so customers can message you directly.
            </p>
          </div>
          <button
            onClick={status?.enabled ? handleDisable : handleEnable}
            disabled={saving}
            className={`rounded-pill px-4 py-2 text-sm font-medium ${
              status?.enabled
                ? 'bg-danger/10 text-danger hover:bg-danger/20'
                : 'bg-brand text-white hover:bg-brand/90'
            }`}
          >
            {saving ? 'Saving...' : status?.enabled ? 'Disable' : 'Enable Widget'}
          </button>
        </div>
      </div>

      {status?.enabled && (
        <>
          {/* Embed Code */}
          <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
            <h3 className="font-display text-base font-semibold text-ink">Embed Code</h3>
            <p className="mt-1 text-sm text-ink-2">
              Add this script tag to your website, just before the closing &lt;/body&gt; tag.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 rounded-xl bg-surface-2 px-4 py-3 text-sm text-ink-2">
                {status.embed_code}
              </code>
              <button
                onClick={copyEmbed}
                className="rounded-xl border border-line p-2.5 hover:bg-black/[0.04]"
                title="Copy embed code"
              >
                {copied ? (
                  <CheckIcon className="h-5 w-5 text-success" />
                ) : (
                  <ClipboardDocumentIcon className="h-5 w-5 text-ink-3" />
                )}
              </button>
            </div>
          </div>

          {/* Appearance */}
          <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
            <h3 className="font-display text-base font-semibold text-ink">Appearance</h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-2">Primary Color</label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded border border-line"
                  />
                  <input
                    type="text"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-28 rounded-xl border border-line bg-surface text-ink px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-2">Welcome Message</label>
                <input
                  type="text"
                  value={welcomeMessage}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-line bg-surface text-ink px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-2">Position</label>
                <select
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  className="mt-1 rounded-xl border border-line bg-surface text-ink px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  <option value="bottom-right">Bottom Right</option>
                  <option value="bottom-left">Bottom Left</option>
                </select>
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
